/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProviderData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading and managing provider-related data (providers, types, models, voices).
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { getProviderModelsByType, getProviderVoices } from '../api/aiProfilesApi';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import type { AiProfile } from '../api/aiProfilesApi';
import type { AiProvider, AzureTTSVoice } from '../types';

type AzureVoiceRaw = {
  Name?: string; ShortName?: string; Gender?: string; Locale?: string;
  DisplayLanguage?: string; Status?: string; VoiceTag?: Record<string, unknown>;
  StyleList?: string[]; SampleRateHertz?: string | number; RolePlayList?: string[];
};

export function useProviderData(formData: Partial<AiProfile>, isEditMode: boolean, handleInputChange: (field: string, value: unknown) => void) {
  const { showError } = useNotifications();
  const { t } = useI18n();

  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<AiProvider | null>(null);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [availableVoices, setAvailableVoices] = useState<string[]>([]);
  const [availableVoicesData, setAvailableVoicesData] = useState<AzureTTSVoice[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);

  const providersRequestRef = useRef<AbortController | null>(null);
  const typesRequestRef = useRef<AbortController | null>(null);
  const modelsRequestRef = useRef<AbortController | null>(null);
  const voicesRequestRef = useRef<AbortController | null>(null);
  const showErrorRef = useRef(showError);
  const translateRef = useRef(t);
  const voicesDataCountRef = useRef(0); // Track voices count to avoid stale closure

  useEffect(() => {
    showErrorRef.current = showError;
    translateRef.current = t;
  }, [showError, t]);

  // Track voices count for stale closure prevention
  useEffect(() => {
    voicesDataCountRef.current = availableVoicesData.length;
  }, [availableVoicesData]);

  // Load providers
  const loadProviders = useCallback(async () => {
    providersRequestRef.current?.abort();
    const controller = new AbortController();
    providersRequestRef.current = controller;

    try {
      const response = await fetch(buildApiUrl('/api/ai/providers'), {
        method: 'GET',
        headers: getApiHeaders(),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      const providersData = result.data || [];

      const mappedProviders = providersData.map((provider: AiProvider) => {
        if (provider.supported_types) {
          return provider;
        }

        const isAzureTTSOnly =
          provider.provider === 'azure' &&
          (provider.name?.toLowerCase().includes('tts') ||
            provider.name?.toLowerCase().includes('text-to-speech') ||
            provider.name?.toLowerCase().includes('voice'));

        if (isAzureTTSOnly) {
          return { ...provider, supported_types: ['tts'] };
        }

        return provider;
      });

      setProviders(mappedProviders);
    } catch (error) {
      const aborted =
        error?.name === 'AbortError' ||
        (error instanceof DOMException && error.name === 'AbortError');

      if (!aborted) {
        showErrorRef.current?.(translateRef.current('aiProfiles.errors.failedToLoadTypesGeneric'));
        setProviders([]);
      }
    } finally {
      if (providersRequestRef.current === controller) {
        providersRequestRef.current = null;
      }
    }
  }, []);

  // Load available types for a provider
  const loadAvailableTypes = useCallback(async (providerId: string | number) => {
    typesRequestRef.current?.abort();

    if (!providerId) {
      setAvailableTypes([]);
      return;
    }

    const controller = new AbortController();
    typesRequestRef.current = controller;

    setLoadingTypes(true);
    try {
      const response = await fetch(buildApiUrl(`/api/ai/providers/${providerId}/supported-types`), {
        method: 'GET',
        headers: getApiHeaders(),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      const types = result.data?.types || result.data?.supported_types || [];
      setAvailableTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      const aborted =
        error?.name === 'AbortError' ||
        (error instanceof DOMException && error.name === 'AbortError');
      if (!aborted) {
        const message =
          error instanceof Error && error.message && error.message.startsWith('HTTP ')
            ? translateRef.current('aiProfiles.errors.failedToLoadTypes')
            : error?.message || translateRef.current('aiProfiles.errors.failedToLoadTypesGeneric');
        showErrorRef.current?.(message);
        setAvailableTypes([]);
      }
    } finally {
      if (typesRequestRef.current === controller) {
        typesRequestRef.current = null;
      }
      setLoadingTypes(false);
    }
  }, []);

  // Load available models for a specific type and provider
  const loadAvailableModelsByType = useCallback(async (providerId: string | number, type: string) => {
    modelsRequestRef.current?.abort();

    if (!providerId || !type) {
      setAvailableModels([]);
      return;
    }

    const controller = new AbortController();
    modelsRequestRef.current = controller;
    setLoadingModels(true);

    try {
      const response = await getProviderModelsByType(providerId);
      const modelsByType = response.data?.models_by_type || {};
      let models = modelsByType[type] || [];

      // Frontend fallback: if agent_realtime returns empty, use static realtime models
      if (type === 'agent_realtime' && (!Array.isArray(models) || models.length === 0)) {
        models = [
          'gpt-realtime-1.5',   // Flagship April 2026
          'gpt-realtime-mini',  // Cost-efficient / low-latency
          'gpt-realtime'        // Base model (backward compatibility)
        ];
      }

      setAvailableModels(Array.isArray(models) ? models : []);
    } catch (error) {
      const aborted =
        error?.name === 'AbortError' ||
        (error instanceof DOMException && error.name === 'AbortError');

      if (!aborted) {
        const message =
          error instanceof Error && error.message && error.message.startsWith('HTTP ')
            ? translateRef.current('aiProfiles.errors.failedToLoadModels')
            : error?.message || translateRef.current('aiProfiles.errors.failedToLoadModelsGeneric');
        showErrorRef.current?.(message);

        // Even on error, provide fallback for agent_realtime
        if (type === 'agent_realtime') {
          setAvailableModels([
            'gpt-realtime-1.5',   // Flagship April 2026
            'gpt-realtime-mini',  // Cost-efficient / low-latency
            'gpt-realtime'        // Base model (backward compatibility)
          ]);
        } else {
          setAvailableModels([]);
        }
      }
    } finally {
      if (modelsRequestRef.current === controller) {
        modelsRequestRef.current = null;
      }
      setLoadingModels(false);
    }
  }, []);

  // Load available voices for TTS models
  const loadAvailableVoices = useCallback(async (providerId: string | number, model?: string, useCache: boolean = true) => {
    if (!providerId) {
      setAvailableVoices([]);
      setAvailableVoicesData([]);
      return;
    }

    const provider = providers.find(p => String(p.id) === String(providerId));

    // Check if we have cached voices in the provider
    if (useCache && provider?.voices_cache && provider.voices_cache.voices) {
      try {
        const cachedVoices = provider.voices_cache.voices;
        let voicesArray = cachedVoices;

        if (typeof voicesArray === 'string') {
          voicesArray = JSON.parse(voicesArray);
        }

        if (Array.isArray(voicesArray) && voicesArray.length === 1 && typeof voicesArray[0] === 'string') {
          voicesArray = JSON.parse(voicesArray[0]);
        }

        // Parse voices for UI display (simplified - full parsing would be very long)
        const voicesData: AzureTTSVoice[] = voicesArray
          .map((voiceObj: AzureVoiceRaw) => {
            if (typeof voiceObj === 'object' && voiceObj.Name) {
              return {
                Name: voiceObj.Name,
                ShortName: voiceObj.ShortName || '',
                Gender: voiceObj.Gender || 'Unknown',
                Locale: voiceObj.Locale || '',
                DisplayLanguage: voiceObj.DisplayLanguage || voiceObj.Locale || '',
                Status: voiceObj.Status || 'GA',
                VoiceTag: voiceObj.VoiceTag || {},
                StyleList: voiceObj.StyleList || [],
                SampleRateHertz: voiceObj.SampleRateHertz ? String(voiceObj.SampleRateHertz) : undefined,
                RolePlayList: voiceObj.RolePlayList || []
              };
            }
            return null;
          })
          .filter(Boolean) as AzureTTSVoice[];

        const voicesList = voicesData.map(v => `${v.Name} (${v.DisplayLanguage || v.Locale}, ${v.ShortName})`);

        setAvailableVoices(voicesList);
        setAvailableVoicesData(voicesData);
        return;
      } catch (error) {
        // Fall through to fetch from API
      }
    }

    // Fetch from API if no cache or cache failed
    voicesRequestRef.current?.abort();
    const controller = new AbortController();
    voicesRequestRef.current = controller;
    setLoadingVoices(true);

    try {
      if (provider?.provider === 'azure') {
        // For Azure, load from models-by-type endpoint
        const response = await fetch(
          buildApiUrl(`/api/ai/profiles/providers/${providerId}/models-by-type?type=tts`),
          {
            method: 'GET',
            headers: getApiHeaders(),
            signal: controller.signal
          }
        );

        if (response.ok) {
          const result = await response.json();
          let voicesArray = result.data?.models || result.data?.tts || [];

          if (Array.isArray(voicesArray) && voicesArray.length === 1 && typeof voicesArray[0] === 'string') {
            try {
              voicesArray = JSON.parse(voicesArray[0]);
            } catch (e) {
              // Ignore parsing error
            }
          }

          // Parse Azure voice objects (simplified)
          const voicesData: AzureTTSVoice[] = voicesArray
            .map((voiceObj: AzureVoiceRaw) => {
              if (typeof voiceObj === 'object' && voiceObj.Name) {
                return {
                  Name: voiceObj.Name,
                  ShortName: voiceObj.ShortName || '',
                  Gender: voiceObj.Gender || 'Unknown',
                  Locale: voiceObj.Locale || '',
                  DisplayLanguage: voiceObj.DisplayLanguage || voiceObj.Locale || '',
                  Status: voiceObj.Status || 'GA',
                  VoiceTag: voiceObj.VoiceTag || {},
                  StyleList: voiceObj.StyleList || [],
                  SampleRateHertz: voiceObj.SampleRateHertz ? String(voiceObj.SampleRateHertz) : undefined,
                  RolePlayList: voiceObj.RolePlayList || []
                };
              }
              return null;
            })
            .filter(Boolean) as AzureTTSVoice[];

          const voicesList = voicesData.map(v => `${v.Name} (${v.DisplayLanguage || v.Locale}, ${v.ShortName})`);

          setAvailableVoices(voicesList);
          setAvailableVoicesData(voicesData);
        }
      } else if (model) {
        // For other providers, use voices endpoint
        const response = await getProviderVoices(providerId, model);
        const voices = response.data?.voices || [];
        setAvailableVoices(voices);
        // Only reset availableVoicesData if we don't have Azure data loaded already
        // This prevents race conditions where provider is temporarily not found
        if (voicesDataCountRef.current === 0) {
          setAvailableVoicesData([]);
        }
      } else {
        // Only reset if we don't have Azure data loaded already
        if (voicesDataCountRef.current === 0) {
          setAvailableVoices([]);
          setAvailableVoicesData([]);
        }
      }
    } catch (error) {
      const aborted =
        error?.name === 'AbortError' ||
        (error instanceof DOMException && error.name === 'AbortError');

      if (!aborted) {
        showErrorRef.current?.(translateRef.current('aiProfiles.errors.failedToLoadVoices'));
      }
      // Only reset voices if we don't have Azure data loaded already
      // and the error wasn't from an aborted request
      if (!aborted && voicesDataCountRef.current === 0) {
        setAvailableVoices([]);
        setAvailableVoicesData([]);
      }
    } finally {
      if (voicesRequestRef.current === controller) {
        voicesRequestRef.current = null;
      }
      setLoadingVoices(false);
    }
  }, [providers]);

  const handleInputChangeRef = useRef(handleInputChange);
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange;
  }, [handleInputChange]);

  // Load providers on mount
  useEffect(() => {
    loadProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update selectedProvider when provider_id changes
  useEffect(() => {
    if (formData.provider_id && providers.length > 0) {
      const provider = providers.find(p => String(p.id) === String(formData.provider_id));
      setSelectedProvider(provider || null);
    } else {
      setSelectedProvider(null);
    }
  }, [formData.provider_id, providers]);

  // Load types when provider changes
  useEffect(() => {
    if (formData.provider_id) {
      loadAvailableTypes(formData.provider_id);
    } else {
      setAvailableTypes([]);
      setAvailableModels([]);
      setAvailableVoices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.provider_id]);

  // Auto-select type when only one type is available
  useEffect(() => {
    if (!isEditMode && availableTypes.length === 1 && formData.provider_id && !formData.type) {
      handleInputChangeRef.current('type', availableTypes[0]);
    }
  }, [availableTypes, formData.provider_id, formData.type, isEditMode]);

  // Load models when type changes
  useEffect(() => {
    if (formData.provider_id && formData.type) {
      loadAvailableModelsByType(formData.provider_id, formData.type);

      // Initialize model for OpenAI TTS
      if (selectedProvider?.provider === 'openai' && formData.type === 'tts' && !formData.model) {
        handleInputChangeRef.current('model', 'tts-1');
      }
    } else {
      setAvailableModels([]);
      setAvailableVoices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.provider_id, formData.type, selectedProvider?.provider, formData.model]);

  // Load voices when model changes (only for TTS)
  useEffect(() => {
    // Find provider directly from providers array to avoid race conditions with selectedProvider
    const currentProvider = formData.provider_id
      ? providers.find(p => String(p.id) === String(formData.provider_id))
      : null;
    const isAzure = currentProvider?.provider === 'azure';

    if (formData.provider_id && isAzure) {
      // For Azure, load voices immediately (only if we don't have them yet)
      if (availableVoicesData.length === 0) {
        loadAvailableVoices(formData.provider_id, undefined, true);
      }
    } else if (formData.provider_id && formData.type === 'tts' && formData.model) {
      loadAvailableVoices(formData.provider_id, formData.model, true);
    } else if (!formData.provider_id) {
      // Only reset if provider_id is actually empty
      setAvailableVoices([]);
      setAvailableVoicesData([]);
    }
    // Note: Do NOT reset voices if provider_id exists but conditions aren't met
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.provider_id, formData.model, formData.type, providers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      providersRequestRef.current?.abort();
      typesRequestRef.current?.abort();
      modelsRequestRef.current?.abort();
      voicesRequestRef.current?.abort();
    };
  }, []);

  return {
    providers,
    selectedProvider,
    availableTypes,
    availableModels,
    availableVoices,
    availableVoicesData,
    loadingTypes,
    loadingModels,
    loadingVoices
  };
}

